import React, { useEffect, useRef, useState } from 'react';
import { get, post, put, api } from '../utils/api';
import { useAuth } from '../App';
import { SUBTYPES, SUBTYPE_LABELS, FOT_SUBTYPES, LOAN_SUBTYPES, UTILITY_PROPERTIES } from '../constants/subtypes';

const MONTHS = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];
const fmt = n => new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0) + ' ₽';
const fmtInt = n => new Intl.NumberFormat('ru-RU').format(Math.round(n || 0)) + ' ₽';

const INTERNAL_COUNTERPARTIES = ['УК', 'Н44', 'Н44-офисы', 'М29', 'М19'];
const fmtDate = d => { if (!d) return d; const [y, m, day] = d.split('-'); return `${day}-${m}-${y.slice(2)}`; };

const TYPES = [
  { value: 'income',           label: 'Доход' },
  { value: 'internal_income',  label: 'Вн. приход' },
  { value: 'expense',          label: 'Расход' },
  { value: 'internal_expense', label: 'Вн. расход' },
  { value: 'dividend',         label: 'Дивиденды' },
];

const PAYMENT_METHODS = {
  income:           ['Наличные','Расчётный счёт','На карту'],
  internal_income:  ['Наличные','Расчётный счёт'],
  expense:          ['Наличные','Расчётный счёт','На карту'],
  internal_expense: ['Наличные','Расчётный счёт'],
  dividend:         ['Наличные','Расчётный счёт','На карту'],
};


const TYPE_BADGE = {
  income:           'badge-green',
  internal_income:  'badge-blue',
  expense:          'badge-red',
  internal_expense: 'badge-blue',
  dividend:         'badge-red',
};
const TYPE_LABEL = {
  income: 'Доход', internal_income: 'Вн. приход',
  expense: 'Расход', internal_expense: 'Вн. расход', dividend: 'Дивиденды',
};

function TxModal({ contracts, utilityTypes, onClose, onSave, initialTx }) {
  const now = new Date();
  const [form, setForm] = useState(initialTx ? {
    type: initialTx.type,
    subtype: initialTx.subtype,
    amount: String(initialTx.amount),
    date: initialTx.date,
    payment_method: initialTx.payment_method,
    contract_id: initialTx.contract_id || '',
    utility_properties: initialTx.utility_property ? [initialTx.utility_property] : [],
    utility_type: initialTx.utility_type || '',
    period_month: initialTx.period_month || now.getMonth() + 1,
    period_year: initialTx.period_year || now.getFullYear(),
    description: initialTx.description || '',
    counterparty: initialTx.counterparty || '',
  } : {
    type: 'income', subtype: 'rent', amount: '',
    date: now.toISOString().slice(0, 10),
    payment_method: 'Наличные', contract_id: '',
    utility_properties: [], utility_type: '',
    period_month: now.getMonth() + 1, period_year: now.getFullYear(),
    description: '', counterparty: '',
  });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [upropOpen, setUpropOpen] = useState(false);
  const upropRef = useRef(null);
  const s = f => setForm(p => ({ ...p, ...f }));

  useEffect(() => {
    if (!upropOpen) return;
    const handler = e => { if (upropRef.current && !upropRef.current.contains(e.target)) setUpropOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [upropOpen]);

  const availableSubtypes = SUBTYPES.filter(st => st.type === form.type && !st.hidden);
  const methods = PAYMENT_METHODS[form.type] || ['Наличные'];
  const isUtility = form.subtype === 'utility';
  const isDepositReturn = form.subtype === 'deposit_return';
  const isDepositPenalty = form.subtype === 'deposit_penalty';
  const isFot = FOT_SUBTYPES.includes(form.subtype);
  const isTenantRefund = form.subtype === 'tenant_refund';
  const needsContract = (form.type === 'income' && (form.subtype === 'rent' || form.subtype === 'deposit')) || isDepositReturn || isDepositPenalty || isTenantRefund;
  const needsPeriod = (form.type === 'income' && form.subtype === 'rent') || isUtility || isFot || isTenantRefund;
  const isLoan = LOAN_SUBTYPES.includes(form.subtype);

  const save = async () => {
    if (saving) return;
    if (!(parseFloat(form.amount) > 0)) return setErr('Укажите сумму больше нуля');
    if (!form.date) return setErr('Укажите дату');
    if (isUtility && form.utility_properties.length === 0) return setErr('Выберите объект');
    if (isUtility && !form.utility_type) return setErr('Выберите вид услуги');
    if (needsContract && (form.subtype === 'rent' || isDepositReturn || isDepositPenalty || isTenantRefund) && !form.contract_id) return setErr('Выберите договор');
    if (isLoan && !form.counterparty) return setErr('Выберите контрагента по займу');
    if ((isUtility || isFot) && !(form.period_month && form.period_year)) return setErr('Укажите период начисления (месяц и год)');
    if ((isDepositReturn || isDepositPenalty) && !initialTx) {
      const msg = isDepositReturn
        ? 'Возврат залога повлечёт расторжение договора (если он ещё активен). Продолжить?'
        : 'Удержание залога (штраф) повлечёт расторжение договора (если он ещё активен). Продолжить?';
      if (!window.confirm(msg)) return;
    }
    setSaving(true);
    try {
      const payload = { ...form, utility_property: form.utility_properties[0] || null, counterparty: form.counterparty || null };
      // Период скрыт в форме — не подставлять текущий месяц; при правке сохраняем то, что было
      if (!needsPeriod) {
        payload.period_month = initialTx ? initialTx.period_month || null : null;
        payload.period_year = initialTx ? initialTx.period_year || null : null;
      }
      if (initialTx) {
        await put(`/transactions/${initialTx.id}`, payload);
      } else if (isUtility && form.utility_properties.length > 1) {
        // Деление с округлением до копеек; остаток — на последний объект (сумма частей == итог)
        const total = Math.round(parseFloat(form.amount) * 100);
        const n = form.utility_properties.length;
        const base = Math.floor(total / n);
        for (let i = 0; i < n; i++) {
          const cents = i === n - 1 ? total - base * (n - 1) : base;
          await post('/transactions', { ...payload, amount: cents / 100, utility_property: form.utility_properties[i] });
        }
      } else {
        await post('/transactions', payload);
      }
      // Выплата дивидендов АК при доходе на карту создаётся автоматически на сервере
      // (жёсткое правило: на «На карту» деньги не задерживаются) — дублировать здесь не нужно.
      onSave();
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-title">{initialTx ? 'Редактировать транзакцию' : 'Новая транзакция'}</div>
        {err && <div className="error-msg" style={{ marginBottom: 16 }}>{err}</div>}
        <div className="form-grid">

          {/* Тип (5 кнопок) */}
          <div className="form-group span-2">
            <label>Тип</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {TYPES.map(t => (
                <button key={t.value}
                  className={`btn btn-sm ${form.type === t.value ? 'btn-primary' : 'btn-ghost'}`}
                  style={{ flex: '1 1 auto' }}
                  onClick={() => {
                    const first = SUBTYPES.find(st => st.type === t.value);
                    s({ type: t.value, subtype: first?.value, payment_method: PAYMENT_METHODS[t.value][0] });
                  }}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Категория */}
          <div className="form-group span-2">
            <label>Категория</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {availableSubtypes.map(st => (
                <button key={st.value}
                  className={`btn btn-sm ${form.subtype === st.value ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => s({ subtype: st.value })}>
                  {st.label}
                </button>
              ))}
            </div>
          </div>

          {/* Сумма + дата */}
          <div className="form-group">
            <label>Сумма (₽) *</label>
            <input type="number" value={form.amount} onChange={e => s({ amount: e.target.value })} placeholder="30000" autoFocus />
          </div>
          <div className="form-group">
            <label>Дата *</label>
            <input type="date" value={form.date} onChange={e => s({ date: e.target.value })} />
          </div>

          {/* Способ оплаты */}
          <div className="form-group">
            <label>Способ оплаты</label>
            <select value={form.payment_method} onChange={e => s({ payment_method: e.target.value })}>
              {methods.map(m => <option key={m}>{m}</option>)}
            </select>
            {form.type === 'income' && form.payment_method === 'На карту' && (
              <div style={{ fontSize: 11, color: 'var(--yellow)', marginTop: 4 }}>⚠ Сумма автоматически будет выведена дивидендами АК</div>
            )}
          </div>

          {/* Договор (аренда / залог / возврат залога) */}
          {needsContract && (
            <div className="form-group">
              <label>Договор (арендатор) *</label>
              {(isDepositReturn || isDepositPenalty) && <div style={{ fontSize: 11, color: 'var(--yellow)', marginBottom: 4 }}>⚠ Договор будет расторгнут при сохранении</div>}
              <select value={form.contract_id} onChange={e => s({ contract_id: e.target.value })}>
                <option value="">Выберите...</option>
                {(isDepositReturn || isDepositPenalty || isTenantRefund
                  ? contracts
                  : contracts.filter(c => c.status === 'active')
                ).map(c => (
                  <option key={c.id} value={c.id}>
                    №{c.studio_number} — {c.last_name} {c.first_name}{c.status !== 'active' ? ' (расторгнут)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Период (аренда / коммунальные) */}
          {needsPeriod && <>
            <div className="form-group">
              <label>{isUtility ? 'Месяц КУ' : (isFot || isTenantRefund) ? 'Месяц начисления' : 'Месяц оплаты'}</label>
              <select value={form.period_month} onChange={e => s({ period_month: parseInt(e.target.value) })}>
                {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Год</label>
              <input type="number" value={form.period_year} onChange={e => s({ period_year: parseInt(e.target.value) })} min="2020" max="2035" />
            </div>
          </>}

          {/* Коммунальные: объект + вид услуги */}
          {isUtility && <>
            <div className="form-group span-2">
              <label>Объект(ы) *</label>
              <div ref={upropRef} style={{ position: 'relative' }}>
                <div onClick={() => setUpropOpen(o => !o)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', padding: '7px 10px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, userSelect: 'none' }}>
                  <span style={{ color: form.utility_properties.length === 0 ? 'var(--text3)' : 'inherit' }}>
                    {form.utility_properties.length === 0 ? 'Выберите объект...' : form.utility_properties.join(', ')}
                  </span>
                  <span style={{ color: 'var(--text3)', fontSize: 10 }}>▼</span>
                </div>
                {upropOpen && (
                  <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, zIndex: 200, padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {UTILITY_PROPERTIES.map(p => (
                      <label key={p} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                        <input type="checkbox"
                          checked={form.utility_properties.includes(p)}
                          onChange={e => {
                            const next = e.target.checked
                              ? [...form.utility_properties, p]
                              : form.utility_properties.filter(x => x !== p);
                            s({ utility_properties: next });
                          }}
                        />
                        {p}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {form.utility_properties.length > 1 && (
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>
                  Сумма разделится поровну: {fmt(parseFloat(form.amount || 0) / form.utility_properties.length)} × {form.utility_properties.length}
                </div>
              )}
            </div>
            <div className="form-group span-2">
              <label>Вид услуги *</label>
              <select value={form.utility_type} onChange={e => s({ utility_type: e.target.value })}>
                <option value="">Выберите вид...</option>
                {utilityTypes.filter(t => t.is_active).map(t => <option key={t.id}>{t.name}</option>)}
              </select>
            </div>
          </>}

          {/* Контрагент по займу */}
          {isLoan && (
            <div className="form-group span-2">
              <label>Контрагент *</label>
              <select value={form.counterparty} onChange={e => s({ counterparty: e.target.value })}>
                <option value="">Выберите контрагента...</option>
                {INTERNAL_COUNTERPARTIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}

          {/* Комментарий */}
          <div className="form-group span-2">
            <label>Комментарий</label>
            <textarea value={form.description} onChange={e => s({ description: e.target.value })} placeholder="Описание..." />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Отмена</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Сохранение…' : 'Сохранить'}</button>
        </div>
      </div>
    </div>
  );
}

export default function TransactionsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canEdit = isAdmin || user?.role === 'manager' || user?.role === 'fin_manager';

  const [txs, setTxs] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [utilityTypes, setUtilityTypes] = useState([]);
  const [modal, setModal] = useState(false);
  const [editTx, setEditTx] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [showDeleted, setShowDeleted] = useState(false);
  const [fromDate, setFromDate] = useState(() => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); });
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [sortCol, setSortCol] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  const toggleSort = col => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };
  const SortTh = ({ col, label, style }) => (
    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}
      onClick={() => toggleSort(col)}>
      {label} <span style={{ opacity: sortCol === col ? 1 : 0.25, fontSize: 10 }}>{sortCol === col ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</span>
    </th>
  );

  const load = () => {
    const q = new URLSearchParams();
    if (typeFilter !== 'all') q.set('type', typeFilter);
    q.set('from', fromDate); q.set('to', toDate);
    if (showDeleted) q.set('show_deleted', 'true');
    get(`/transactions?${q}`).then(setTxs).catch(() => {});
    Promise.all([get('/contracts'), get('/contracts?status=terminated'), get('/contracts?status=closed')])
      .then(([active, terminated, closed]) => setContracts([...active, ...terminated, ...closed]))
      .catch(() => {});
  };

  useEffect(() => { load(); }, [typeFilter, fromDate, toDate, showDeleted]);
  useEffect(() => { get('/utility-types').then(setUtilityTypes).catch(() => {}); }, []);

  const remove = async (id) => {
    if (!window.confirm('Удалить транзакцию? Операция будет помечена как удалённая.')) return;
    try { await api(`/transactions/${id}`, { method: 'DELETE' }); load(); }
    catch (e) { window.alert(e.message || 'Не удалось удалить'); }
  };

  const restore = async (id) => {
    try { await api(`/transactions/${id}/restore`, { method: 'POST' }); load(); }
    catch (e) { window.alert(e.message || 'Не удалось восстановить'); }
  };

  const active = txs.filter(t => !t.is_deleted);
  const totalIncome  = active.filter(t => t.type === 'income' && t.subtype !== 'deposit').reduce((s, t) => s + t.amount, 0);
  const totalExpense = active.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
  const totalDividend = active.filter(t => t.type === 'dividend').reduce((s, t) => s + t.amount, 0);

  const descCell = (t) => {
    if (t.type === 'income' && t.subtype === 'rent') return (
      <><div style={{ fontWeight: 600 }}>{t.tenant_name || '—'}</div>
      <div style={{ fontSize: 12, color: 'var(--text3)' }}>Студия {t.studio_number} · {MONTHS[(t.period_month || 1) - 1]} {t.period_year}</div></>
    );
    if (t.subtype === 'utility') return (
      <><div style={{ fontWeight: 600 }}>{t.utility_type}</div>
      <div style={{ fontSize: 12, color: 'var(--text3)' }}>
        {[t.utility_property || t.property_number, t.period_month && t.period_year ? `${MONTHS[t.period_month - 1]} ${t.period_year}` : null].filter(Boolean).join(' · ')}
      </div></>
    );
    if (t.subtype === 'tenant_refund') return (
      <><div style={{ fontWeight: 600 }}>{t.tenant_name || 'Возврат средств арендатору'}</div>
      <div style={{ fontSize: 12, color: 'var(--text3)' }}>
        {[t.studio_number ? `Студия ${t.studio_number}` : null, t.period_month && t.period_year ? `${MONTHS[t.period_month - 1]} ${t.period_year}` : null].filter(Boolean).join(' · ')}
      </div></>
    );
    const isFotTx = FOT_SUBTYPES.includes(t.subtype);
    if (isFotTx) return (
      <><div style={{ fontWeight: 600 }}>{SUBTYPE_LABELS[t.subtype]}</div>
      {t.period_month && t.period_year
        ? <div style={{ fontSize: 12, color: 'var(--text3)' }}>Начислено: {MONTHS[t.period_month - 1]} {t.period_year}</div>
        : t.description && <div style={{ fontSize: 12, color: 'var(--text3)' }}>{t.description}</div>
      }</>
    );
    const isLoanTx = LOAN_SUBTYPES.includes(t.subtype);
    if (isLoanTx) return (
      <><div style={{ fontWeight: 600 }}>{SUBTYPE_LABELS[t.subtype] || t.subtype}</div>
      <div style={{ fontSize: 12, color: 'var(--text3)' }}>{t.counterparty || '—'}</div></>
    );
    return (
      <><div style={{ fontWeight: 600 }}>{SUBTYPE_LABELS[t.subtype] || t.subtype || '—'}</div>
      {t.description && <div style={{ fontSize: 12, color: 'var(--text3)' }}>{t.description}</div>}
      {t.tenant_name && t.subtype === 'deposit' && <div style={{ fontSize: 12, color: 'var(--text3)' }}>{t.tenant_name}</div>}</>
    );
  };

  const isInflow = t => t.type === 'income' || t.type === 'internal_income';

  const sorted = [...txs].sort((a, b) => {
    let va, vb;
    switch (sortCol) {
      case 'date':   va = a.date; vb = b.date; break;
      case 'amount': va = (isInflow(a) ? 1 : -1) * a.amount; vb = (isInflow(b) ? 1 : -1) * b.amount; break;
      case 'type':   va = a.type + (a.subtype || ''); vb = b.type + (b.subtype || ''); break;
      case 'method': va = a.payment_method || ''; vb = b.payment_method || ''; break;
      default: return 0;
    }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Транзакции</h1>
        <button className="btn btn-primary" onClick={() => setModal(true)}>+ Добавить</button>
      </div>

      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
        <div className="stat-card"><div className="stat-label">Доходы (P&L)</div><div className="stat-value green">{fmtInt(totalIncome)}</div></div>
        <div className="stat-card"><div className="stat-label">Расходы (P&L)</div><div className="stat-value red">{fmtInt(totalExpense)}</div></div>
        <div className="stat-card"><div className="stat-label">Прибыль</div><div className={`stat-value ${totalIncome - totalExpense >= 0 ? 'green' : 'red'}`}>{fmtInt(totalIncome - totalExpense)}</div></div>
        <div className="stat-card"><div className="stat-label">Дивиденды выплачено</div><div className="stat-value red">{fmtInt(totalDividend)}</div></div>
      </div>

      <div className="filters">
        <div className="form-group"><label>С</label><input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} /></div>
        <div className="form-group"><label>По</label><input type="date" value={toDate} onChange={e => setToDate(e.target.value)} /></div>
        <div className="form-group"><label>Тип</label>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="all">Все</option>
            <option value="income">Доходы</option>
            <option value="internal_income">Вн. приходы</option>
            <option value="expense">Расходы</option>
            <option value="internal_expense">Вн. расходы</option>
            <option value="dividend">Дивиденды</option>
          </select>
        </div>
        {isAdmin && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: showDeleted ? 'var(--red)' : 'var(--text2)', cursor: 'pointer', marginTop: 22 }}>
            <input type="checkbox" checked={showDeleted} onChange={e => setShowDeleted(e.target.checked)} />
            Показать удалённые
          </label>
        )}
      </div>

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <SortTh col="date" label="Дата" />
                <SortTh col="type" label="Тип" />
                <th>Описание</th>
                <SortTh col="method" label="Способ" />
                <SortTh col="amount" label="Сумма" style={{ textAlign: 'right' }} />
                {canEdit && <th></th>}
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text3)', padding: 40 }}>Нет транзакций за период</td></tr>}
              {sorted.map(t => (
                <tr key={t.id} style={{ opacity: t.is_deleted ? 0.5 : 1, background: t.is_deleted ? 'rgba(239,68,68,0.05)' : undefined }}>
                  <td style={{ color: 'var(--text2)', whiteSpace: 'nowrap' }}>{fmtDate(t.date)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <span className={`badge ${TYPE_BADGE[t.type] || 'badge-blue'}`}>{TYPE_LABEL[t.type] || t.type}</span>
                      {t.subtype && <span className="badge badge-blue">{SUBTYPE_LABELS[t.subtype] || t.subtype}</span>}
                      {!!t.is_deleted && <span className="badge badge-red">Удалено</span>}
                    </div>
                  </td>
                  <td>
                    {descCell(t)}
                    {!!t.is_deleted && <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 2 }}>Удалил: {t.deleted_by_name} · {t.deleted_at?.slice(0, 10)}</div>}
                  </td>
                  <td><span className="badge badge-blue">{t.payment_method}</span></td>
                  <td style={{ fontWeight: 700, color: isInflow(t) ? 'var(--green)' : 'var(--red)', whiteSpace: 'nowrap' }}>
                    {isInflow(t) ? '+' : '-'}{fmt(t.amount)}
                  </td>
                  {canEdit && (
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {!t.is_deleted && <button className="btn btn-ghost btn-sm" onClick={() => setEditTx(t)} title="Редактировать">✎</button>}
                        {(isAdmin || (user?.role === 'fin_manager' && t.payment_method !== 'cash')) && (t.is_deleted
                          ? <button className="btn btn-ghost btn-sm" onClick={() => restore(t.id)} title="Восстановить">↩</button>
                          : <button className="btn btn-danger btn-sm" onClick={() => remove(t.id)}>✕</button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && <TxModal contracts={contracts} utilityTypes={utilityTypes} onClose={() => setModal(false)} onSave={() => { setModal(false); load(); }} />}
      {editTx && <TxModal contracts={contracts} utilityTypes={utilityTypes} initialTx={editTx} onClose={() => setEditTx(null)} onSave={() => { setEditTx(null); load(); }} />}
    </div>
  );
}
